import {RequestResult, RequestResultValue} from "../../common/api/requestResult";
import {HttpService} from "../../common/api/httpService";
import {
    AdminUserBalanceHistory,
    AdminUserFullInfo,
    AdminUserInfoResult,
    AdminUserStatisticInfoResult,
    InventorySkinInfoResult,
    AdminUserPenaltyInfo,
    AdminUserPenaltyHistory,
    GetPenaltyHistoryRequest,
    AdminUserSessionsResponse,
    AdminSessionIpsResponse,
    AdminUserFundsBucketRow
} from "../../models/user";
import {UserOperationInfoResult, UserOperationStatus, UserOperationType} from "../../models/userOperation";
import JSONbig from 'json-bigint';
import {PaginationResponse} from "../../models/paginationResponse";
import { PaymentOperationType } from "../../models/paymentMethod";
const JSONbigNative = JSONbig({ useNativeBigInt: true });

export async function usersListApi(filterSearch:string, orderBy:number, filterOnlineState: number, filterTradingState: number, page:number, count:number, filterRoles: string[] = [])
    : Promise<RequestResultValue<AdminUserInfoResult>> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/list`, JSONbig.stringify(
        {
            page: page,
            count: count,
            search: filterSearch || undefined,
            isOnline: filterOnlineState > -1 ? filterOnlineState===1 : undefined,
            isTrading: filterTradingState > -1 ? filterTradingState===1 : undefined,
            roles: filterRoles.length ? filterRoles : undefined,
            orderBy: orderBy
        }
    ));
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminUserInfoResult>(true, body as AdminUserInfoResult);
    }
    return new RequestResultValue<AdminUserInfoResult>(false);
}

export async function usersStatisticsApi(filterSearch:string, orderBy:number, page:number, count:number): Promise<RequestResultValue<AdminUserStatisticInfoResult>> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/statistics`, JSONbig.stringify(
        {
            page: page,
            count: count,
            steamIds: filterSearch?[BigInt(filterSearch)]:[],
            orderBy: orderBy
        }
    ));
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminUserStatisticInfoResult>(true, body as AdminUserStatisticInfoResult);
    }
    return new RequestResultValue<AdminUserStatisticInfoResult>(false);
}

export async function userFullInfoApi(id:bigint): Promise<RequestResultValue<AdminUserFullInfo>> {
    const response = await HttpService.getInstance().requestGetAsync(`admin/users/${id}/full-info`);
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminUserFullInfo>(true, body as AdminUserFullInfo);
    }
    return new RequestResultValue<AdminUserFullInfo>(false);
}

export async function userBalanceHistoryApi(id:bigint, from?: Date, to?: Date): Promise<RequestResultValue<AdminUserBalanceHistory[]>> {
    let url = `admin/users/${id}/balance-history`;
    const params: string[] = [];
    if (from) params.push(`from=${from.toISOString()}`);
    if (to) params.push(`to=${to.toISOString()}`);
    if (params.length > 0) {
        url += `?${params.join('&')}`;
    }
    const response = await HttpService.getInstance().requestGetAsync(url);
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminUserBalanceHistory[]>(true, body as AdminUserBalanceHistory[]);
    }
    return new RequestResultValue<AdminUserBalanceHistory[]>(false);
}

export async function userRemoveBansApi(id:bigint): Promise<RequestResult> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/${id}/remove-bans`, '{}');
    if (response.status >= 200 && response.status < 300) {
        return new RequestResult(true);
    }
    return new RequestResult(false);
}

export interface UserInventoryFilter {
    ids?: string[];
    assetStringIds?: string[];
    statuses?: number[];
}

export async function userInventoryApi(userId:bigint, gameId:number, page:number, count:number, filter?: UserInventoryFilter)
    : Promise<RequestResultValue<InventorySkinInfoResult>> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/${userId}/inventory`, JSONbig.stringify(
        {
            page: page,
            count: count,
            gameId: gameId,
            filter: filter ?? {}
        }
    ));
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<InventorySkinInfoResult>(true, body as InventorySkinInfoResult);
    }
    return new RequestResultValue<InventorySkinInfoResult>(false);
}

export async function userOperationsApi(userId:bigint, page:number, count:number, 
    options?: { types?: UserOperationType[], statuses?: UserOperationStatus[], range?: { min?: string, max?: string }, gameId?: number, search?: string })
    : Promise<RequestResultValue<UserOperationInfoResult>> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/${userId}/operations`, JSONbig.stringify(
        {
            page: page,
            count: count,
            types: options?.types,
            statuses: options?.statuses,
            range: options?.range ? { min: options.range.min, max: options.range.max } : undefined,
            gameId: options?.gameId,
            search: options?.search
        }
    ));
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<UserOperationInfoResult>(true, body as UserOperationInfoResult);
    }
    return new RequestResultValue<UserOperationInfoResult>(false);
}

export async function userPenaltyInfoApi(userId: bigint): Promise<RequestResultValue<AdminUserPenaltyInfo>> {
    const response = await HttpService.getInstance().requestGetAsync(`admin/users/${userId}/penalty/info`);
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminUserPenaltyInfo>(true, body as AdminUserPenaltyInfo);
    }
    return new RequestResultValue<AdminUserPenaltyInfo>(false);
}

export async function userPenaltyUnbanApi(userId: bigint): Promise<RequestResult> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/${userId}/penalty/unban/`, '{}');
    if (response.status >= 200 && response.status < 300) {
        return new RequestResult(true);
    }
    return new RequestResult(false);
}

export interface UnbanAllPenaltiesResponse {
    unbannedCount: number;
    failedCount: number;
}

export interface AdminPenaltySummary {
    bannedUsersCount: number;
    finedWithoutBanUsersCount: number;
}

export async function penaltySummaryApi(): Promise<RequestResultValue<AdminPenaltySummary>> {
    const response = await HttpService.getInstance().requestGetAsync(`admin/users/penalty/summary`);
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminPenaltySummary>(true, body as AdminPenaltySummary);
    }
    return new RequestResultValue<AdminPenaltySummary>(false);
}

export async function unbanAllPenaltiesApi(): Promise<RequestResultValue<UnbanAllPenaltiesResponse>> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/penalty/unban-all`, '{}');
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<UnbanAllPenaltiesResponse>(true, body as UnbanAllPenaltiesResponse);
    }
    return new RequestResultValue<UnbanAllPenaltiesResponse>(false);
}

export async function userPenaltyHistoryApi(userId: bigint, request: GetPenaltyHistoryRequest): Promise<RequestResultValue<PaginationResponse<AdminUserPenaltyHistory>>> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/${userId}/penalty/history`, JSONbig.stringify(request));
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<PaginationResponse<AdminUserPenaltyHistory>>(true, body as PaginationResponse<AdminUserPenaltyHistory>);
    }
    return new RequestResultValue<PaginationResponse<AdminUserPenaltyHistory>>(false);
}

export async function userSessionsApi(userId: bigint, page: number, count: number): Promise<RequestResultValue<AdminUserSessionsResponse>> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/${userId}/sessions`, JSONbig.stringify({ page, count }));
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminUserSessionsResponse>(true, body as AdminUserSessionsResponse);
    }
    return new RequestResultValue<AdminUserSessionsResponse>(false);
}

export async function userSessionIpsApi(userId: bigint, sessionId: number): Promise<RequestResultValue<AdminSessionIpsResponse>> {
    const response = await HttpService.getInstance().requestGetAsync(`admin/users/${userId}/sessions/${sessionId}/ips`);
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<AdminSessionIpsResponse>(true, body as AdminSessionIpsResponse);
    }
    return new RequestResultValue<AdminSessionIpsResponse>(false);
}

export async function updateUserSteamApiKey(userId: bigint, steamApiKey: string): Promise<RequestResult> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/steam-api-key`, JSONbig.stringify({ userId, steamApiKey: steamApiKey }));
    if (response.status >= 200 && response.status < 300) {
        return new RequestResult(true);
    }
    return new RequestResult(false);
}


export async function updateUserTradeLink(userId: bigint, tradeLink: string): Promise<RequestResult> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/trade-link`, JSONbig.stringify({ userId, tradeLink: tradeLink }));
    if (response.status >= 200 && response.status < 300) {
        return new RequestResult(true);
    }
    return new RequestResult(false);
}

export async function userBalanceChangeApi(userId: bigint, amountInKopeks: number, operationType: PaymentOperationType, allowImmediateWithdrawal?: boolean): Promise<RequestResult> {
    const payload = { userId, amount: amountInKopeks, operationType, allowImmediateWithdrawal } as any;
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/balance-changes`, JSONbig.stringify(payload));
    if (response.status >= 200 && response.status < 300) {
        return new RequestResult(true);
    }
    return new RequestResult(false);
}

export async function createFakeUserApi(steamId: bigint): Promise<RequestResult> {
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/create`, JSONbig.stringify({ steamId }));
    if (response.status >= 200 && response.status < 300) {
        return new RequestResult(true);
    }
    return new RequestResult(false);
}

export async function userFundsBucketsApi(userId: bigint, page: number, count: number, from?: Date, to?: Date)
    : Promise<RequestResultValue<PaginationResponse<AdminUserFundsBucketRow>>> {
    const payload: any = { page, count };
    if (from) payload.from = from;
    if (to) payload.to = to;
    const response = await HttpService.getInstance().requestPostAsync(`admin/users/${userId}/funds-buckets`, JSONbig.stringify(payload));
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<PaginationResponse<AdminUserFundsBucketRow>>(true, body as PaginationResponse<AdminUserFundsBucketRow>);
    }
    return new RequestResultValue<PaginationResponse<AdminUserFundsBucketRow>>(false);
}

export async function getWebhookDataApi(refId: number, paymentOperationType: PaymentOperationType): Promise<RequestResultValue<string | null>> {
    const response = await HttpService.getInstance().requestGetAsync(`admin/Payments/webhook-data?refId=${refId}&paymentOperationType=${paymentOperationType}`);
    const text = await response.text();
    if (response.status === 200) {
        // API возвращает строку данных
        return new RequestResultValue<string | null>(true, text);
    } else if (response.status === 204) {
        // Данных нет
        return new RequestResultValue<string | null>(true, null);
    }
    return new RequestResultValue<string | null>(false);
}

export async function manualHandlePaymentApi(paymentId: string, userOperationId: number): Promise<RequestResult> {
    const payload = {
        paymentId,
        userOperationId
    };

    const response = await HttpService.getInstance().requestPostAsync(
        `admin/payments/manual-handle`,
        JSONbig.stringify(payload)
    );

    if (response.status >= 200 && response.status < 300) {
        return new RequestResult(true);
    }

    return new RequestResult(false);
}